import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Space, Tag, Modal, message, Image, Tooltip, Select } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash, faNewspaper } from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";
import postApi from "../../../api/postApi";
import { audienceColumn } from "../../../components/audience-fields";

const API_IMG_URL = process.env.REACT_APP_API_PUBLIC_URL || "";
const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL || "";

const STATUS_MAP = {
    2: { text: "Đã đăng", color: "green" },
    3: { text: "Đã xóa", color: "red" },
};

// Ngoài bài blog ('guide') còn có bài "khung bình luận" của từng trang sàn
// (postId 10/11/12/64). Trước đây list lọc cứng category=guide nên các bài này
// biến mất khỏi admin, không cách nào mở ra sửa nội dung.
const CATEGORY_MAP = {
    guide: { text: "Blog", color: "blue" },
    shopee: { text: "Shopee", color: "orange" },
    lazada: { text: "Lazada", color: "geekblue" },
    tiktok: { text: "TikTok", color: "purple" },
    shopeefood: { text: "ShopeeFood", color: "red" },
    videolist: { text: "Video", color: "cyan" },
};

function PostList() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState("all");

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const { data } = await postApi.getBlogPosts("sort=-createTime&limit=200");
            setPosts(data.post || []);
        } catch {
            message.error("Không thể tải danh sách bài viết");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPosts(); }, []);

    const handleDelete = (postId) => {
        Modal.confirm({
            title: "Xác nhận xóa",
            content: "Bài viết sẽ bị ẩn khỏi website. Bạn có chắc không?",
            okText: "Xóa",
            cancelText: "Hủy",
            okType: "danger",
            onOk: async () => {
                try {
                    await postApi.deleteBlogPost(postId);
                    message.success("Đã xóa bài viết");
                    fetchPosts();
                } catch {
                    message.error("Không thể xóa bài viết");
                }
            },
        });
    };

    const columns = [
        { title: "ID", dataIndex: "postId", key: "postId", width: 60 },
        {
            title: "Ảnh",
            dataIndex: "imagePath",
            key: "imagePath",
            width: 90,
            render: (path) =>
                path ? (
                    <Image
                        src={`${API_IMG_URL}/img/post-thumbs/${path}`}
                        alt="thumb"
                        width={80}
                        height={45}
                        style={{ objectFit: "cover", borderRadius: 6 }}
                        fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg=="
                    />
                ) : (
                    <span className="text-content-3">—</span>
                ),
        },
        {
            title: "Tiêu đề",
            dataIndex: "title",
            key: "title",
            ellipsis: true,
            render: (title, record) => (
                <a
                    href={`${FRONTEND_URL}/post/${record.postId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-medium"
                >
                    {title}
                </a>
            ),
        },
        {
            title: "Mô tả",
            dataIndex: "summary",
            key: "summary",
            width: 200,
            render: (summary) => (
                <span title={summary} className="text-content-3 text-xs">
                    {summary?.length > 100 ? summary.slice(0, 100) + "…" : summary || "—"}
                </span>
            ),
        },
        {
            title: "Danh mục",
            dataIndex: "category",
            key: "category",
            width: 110,
            render: (cat) => {
                const c = CATEGORY_MAP[cat] || { text: cat || "—", color: "default" };
                return <Tag color={c.color}>{c.text}</Tag>;
            },
        },
        audienceColumn(),
        {
            title: "Ngày tạo",
            dataIndex: "createTime",
            key: "createTime",
            width: 130,
            render: (ts) => (ts ? format(new Date(ts), "dd/MM/yy HH:mm") : "—"),
        },
        {
            title: "Trạng thái",
            dataIndex: "status",
            key: "status",
            width: 110,
            render: (status) => {
                const s = STATUS_MAP[status] || { text: `#${status}`, color: "default" };
                return <Tag color={s.color}>{s.text}</Tag>;
            },
        },
        {
            title: "Hành động",
            key: "action",
            width: 90,
            fixed: "right",
            render: (_, record) => (
                <Space size={4}>
                    <Tooltip title="Sửa (mở editor)">
                        <Button
                            size="small"
                            icon={<FontAwesomeIcon icon={faEdit} />}
                            onClick={() =>
                                window.open(`${FRONTEND_URL}/post/post_editor/${record.postId}`, "_blank")
                            }
                        />
                    </Tooltip>
                    <Tooltip title="Xóa">
                        <Button
                            size="small"
                            danger
                            icon={<FontAwesomeIcon icon={faTrash} />}
                            onClick={() => handleDelete(record.postId)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const visiblePosts = useMemo(
        () => (category === "all" ? posts : posts.filter((p) => (p.category || "") === category)),
        [posts, category]
    );
    const published = visiblePosts.filter((p) => p.status === 2).length;

    // Chỉ liệt kê danh mục thực sự có bài, tránh option chết.
    const categoryOptions = useMemo(() => {
        const cats = [...new Set(posts.map((p) => p.category || "").filter(Boolean))];
        return [
            { value: "all", label: "Tất cả danh mục" },
            ...cats.map((c) => ({ value: c, label: CATEGORY_MAP[c]?.text || c })),
        ];
    }, [posts]);

    return (
        <div className="flex-1 p-6 overflow-auto">
            <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
                <h1 className="text-2xl font-bold">Quản lý Bài viết Blog</h1>
                <Select
                    value={category}
                    onChange={setCategory}
                    options={categoryOptions}
                    style={{ minWidth: 180 }}
                />
                <Button
                    type="primary"
                    icon={<FontAwesomeIcon icon={faPlus} className="mr-1" />}
                    onClick={() => window.open(`${FRONTEND_URL}/post/post_editor`, "_blank")}
                >
                    Tạo bài viết
                </Button>
            </div>

            {/* Stats */}
            <div className="flex gap-3 mb-5">
                {[
                    { icon: faNewspaper, iconClass: "text-blue-400", value: visiblePosts.length, label: "Tổng bài viết" },
                    { color: "green", value: published, label: "Đã đăng" },
                    { color: "red", value: visiblePosts.length - published, label: "Đã xóa" },
                ].map((stat, i) => (
                    <div key={i} className="bg-surface rounded-lg border border-line shadow-sm px-4 py-3 flex items-center gap-3 min-w-[120px]">
                        {stat.icon ? (
                            <FontAwesomeIcon icon={stat.icon} className={`text-xl ${stat.iconClass}`} />
                        ) : (
                            <span className={`w-3 h-3 rounded-full`} style={{ backgroundColor: stat.color }} />
                        )}
                        <div>
                            <div className="text-2xl font-bold text-content">{stat.value}</div>
                            <div className="text-xs text-content-3">{stat.label}</div>
                        </div>
                    </div>
                ))}
            </div>

            <Table
                columns={columns}
                dataSource={visiblePosts}
                loading={loading}
                rowKey="postId"
                pagination={{ pageSize: 20 }}
                scroll={{ x: "max-content" }}
                size="middle"
            />
        </div>
    );
}

export default PostList;
